// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { NumericFormat } from 'react-number-format';
import { Input, Label, Translation } from '@shared/ui-kit';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { getCurrencySymbol } from '@shared/portfolio-operations/utils/currency';

export interface RefundFieldsDisplayProps {
  percentage: number;
  shares: number;
  amount: number;
  currency: string;
  'data-testid'?: string;
}

export const RefundFieldsDisplay = ({
  percentage,
  shares,
  amount,
  currency,
  'data-testid': dataTestId,
}: RefundFieldsDisplayProps) => {
  const currencyPrefix = getCurrencySymbol(currency);

  return (
    <div
      className="flex flex-1 items-start justify-end gap-4"
      data-testid={dataTestId}
    >
      <div className="flex flex-col gap-1" data-testid="percentage-field">
        <Label className="text-center" data-testid="percentage-label">
          %
        </Label>
        <NumericFormat
          customInput={Input}
          value={percentage}
          name="percentage"
          readOnly
          className="w-[70px] text-center"
          data-testid="percentage-input"
          allowNegative={false}
          decimalScale={2}
          suffix="%"
          allowedDecimalSeparators={DECIMAL_SEPARATORS}
        />
      </div>
      <div className="flex flex-col gap-1" data-testid="shares-field">
        <Label className="text-center" data-testid="shares-label">
          <Translation ns="components" i18nKey="ticker-modal.shares-label" />
        </Label>
        <NumericFormat
          customInput={Input}
          value={shares}
          name="shares"
          readOnly
          className="w-[120px] text-center"
          data-testid="shares-input"
          allowNegative={false}
          decimalScale={2}
          allowedDecimalSeparators={DECIMAL_SEPARATORS}
        />
      </div>
      <div className="flex flex-col gap-1" data-testid="amount-field">
        <Label className="text-center" data-testid="amount-label">
          <Translation ns="components" i18nKey="ticker-modal.amount-label" />
        </Label>
        <NumericFormat
          customInput={Input}
          value={amount}
          name="amount"
          readOnly
          className="w-[120px] text-center"
          data-testid="amount-input"
          prefix={currencyPrefix}
          allowNegative={false}
          decimalScale={0}
          fixedDecimalScale={false}
          allowedDecimalSeparators={DECIMAL_SEPARATORS}
          thousandSeparator
        />
      </div>
    </div>
  );
};
